/**
 * GCP Platform - One-Click Connect
 */
import axios from 'axios'
import { AlertCircle, Cloud, Database, HardDrive, Loader2, Plus, RefreshCw, Server, Trash2, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface GCPAccount {
  id: number
  project_id: string
  label: string
  functions_count: number
  gcs_buckets: number
  firestore_collections: number
  vertex_models: number
  status: string
  connected_at: string
}

interface ConnectForm {
  service_account_json: string
  project_id: string
  label: string
}

export default function GCPPage() {
  const [accounts, setAccounts] = useState<GCPAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<ConnectForm>({ service_account_json: '', project_id: '', label: '' })

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get<{ accounts: GCPAccount[] }>(`${API_URL}/api/aicloud/gcp/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAccounts(resp.data.accounts ?? [])
    } catch {
      setAccounts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchAccounts() }, [])

  const handleConnect = async () => {
    if (!form.service_account_json || !form.project_id) {
      setError('Service Account JSON and Project ID are required')
      return
    }
    try {
      JSON.parse(form.service_account_json)
    } catch {
      setError('Service Account JSON is not valid JSON')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/gcp/connect`, form, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setShowAdd(false)
      setForm({ service_account_json: '', project_id: '', label: '' })
      void fetchAccounts()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Failed to connect')
    } finally {
      setSaving(false)
    }
  }

  const handleSync = async (id: number) => {
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/gcp/accounts/${id}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      void fetchAccounts()
    } catch {
      setError('Sync failed')
    }
  }

  const handleDisconnect = async (id: number) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/aicloud/gcp/accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAccounts(prev => prev.filter(a => a.id !== id))
    } catch {
      setError('Failed to disconnect')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800 }}>Google Cloud Platform</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Connect your GCP projects for functions, storage, and Vertex AI.</p>
        </div>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: '#4285f4', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
            <Plus size={16} /> Add GCP Project
          </button>
        )}
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', color: '#ef4444', fontSize: '13px', marginBottom: '20px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {showAdd && (
        <div style={{ padding: '24px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '16px', marginBottom: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Project ID</label>
            <input type="text" placeholder="my-gcp-project-123" value={form.project_id}
              onChange={e => setForm(p => ({ ...p, project_id: e.target.value }))}
              style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Service Account JSON</label>
            <textarea
              placeholder='Paste the contents of your service account JSON key file...'
              value={form.service_account_json}
              onChange={e => setForm(p => ({ ...p, service_account_json: e.target.value }))}
              rows={5}
              style={{ width: '100%', padding: '12px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'monospace', outline: 'none', resize: 'vertical' }}
            />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Project Label</label>
            <input type="text" placeholder="e.g. My AI Research" value={form.label}
              onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
              style={{ width: '100%', padding: '11px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => void handleConnect()} disabled={saving}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: '#4285f4', color: 'white', border: 'none', borderRadius: '10px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 700 }}>
              {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={16} />}
              {saving ? 'Connecting GCP...' : 'Connect Project'}
            </button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: '12px 24px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', cursor: 'pointer', fontSize: '14px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#4285f4', margin: '0 auto' }} />
        </div>
      ) : accounts.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', background: 'var(--card-bg)', border: '2px dashed var(--border-color)', borderRadius: '20px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(66,133,244,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Cloud size={32} color="#4285f4" />
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>No GCP Projects</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '300px', margin: '0 auto 20px' }}>Upload your Service Account JSON to integrate Google Cloud resources.</p>
          <button onClick={() => setShowAdd(true)} style={{ background: '#4285f4', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Setup GCP
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {accounts.map(acc => (
            <div key={acc.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border-color)', background: 'linear-gradient(135deg, rgba(66,133,244,0.06) 0%, transparent 100%)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'rgba(66,133,244,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Server size={22} color="#4285f4" />
                  </div>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: 700 }}>{acc.label || acc.project_id}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{acc.project_id}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => void handleSync(acc.id)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    <RefreshCw size={13} /> Sync
                  </button>
                  <button onClick={() => void handleDisconnect(acc.id)} style={{ padding: '8px', color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', padding: '24px' }}>
                {[
                  { label: 'Cloud Functions', count: acc.functions_count, icon: <Zap size={18} />, color: '#4285f4' },
                  { label: 'GCS Buckets', count: acc.gcs_buckets, icon: <HardDrive size={18} />, color: '#10b981' },
                  { label: 'Firestore', count: acc.firestore_collections, icon: <Database size={18} />, color: '#6366f1' },
                  { label: 'Vertex Models', count: acc.vertex_models, icon: <Cloud size={18} />, color: '#f59e0b' },
                ].map(r => (
                  <div key={r.label} style={{ padding: '16px', background: 'var(--body-bg)', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                    <div style={{ marginBottom: '8px', color: r.color }}>{r.icon}</div>
                    <div style={{ fontSize: '24px', fontWeight: 800 }}>{r.count}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>{r.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
